import Vue from 'vue';
import { mapGetters } from 'vuex';
import { guid } from '@/mixins/guid';
import { modals } from '@/mixins/modal';
import { uploader } from '@/mixins/aws/uploader/s3';
import uploadProgress from '@/mixins/aws/uploader';
import path from 'path';
import _ from 'lodash';

export default Vue.component('edit-audio-player', {
  template: require('./template.html'),
  mixins: [guid, modals, uploader],
  components: {
    uploadProgress,
  },
  computed: {
    ...mapGetters(['viewport', 'currentSite']),
    displayName() {
      const url = this.droplet.audioFile;
      return url.split('/').pop();
    },
  },
  data() {
    return {
      loading: false,
      // our s3 site buckets are structured this way
      prefix: 'drz_audio_',
      bucketPath: '_droplet-assets/audioPlayer/',
      s3Url: process.env.S3_ASSETS_URL,
      audioFiles: [],
      confirmModal: false,
      activeAudio: null,
      showFiles: false,
    };
  },
  name: 'edit-audio-player',
  props: ['droplet', 'show-edit-bubble'],
  methods: {
    matchFile(file) {
      const f = file.split('/').pop();
      const media = this.droplet.audioFile.split('/').pop();
      return f === media;
    },
    toggleBrowse() {
      this.showFiles = !this.showFiles;
      if (this.showFiles) {
        this.fetchAudioFiles();
      }
    },
    openUpload() {
      this.$refs.uploadFile.click();
    },
    async uploadAudio(e) {
      this.$validator.validateAll().then(async (result) => {
        // run only if file types and dimensions are correct
        if (result) {
          let newFile = [...e.target.files];
          if (newFile.length > 0) {
            newFile = newFile[0];
            // clean up the file name for s3
            let fileName = path.basename(newFile.name);
            fileName = `${this.prefix}${_.trim(fileName)}`;
            fileName = _.replace(fileName, /\s+/g, '_');

            try {
              // send off to our s3 upload mixin
              const filePath = `/${this.bucketPath}${fileName}`;
              await this.uploadFile({
                file: `${this._sitePath}${filePath}`,
                body: newFile,
              });
              await this.updateAudioSrc(filePath);
            } catch (err) {
              if (!err.message.match(/aborted/gi)) {
                this.$log.error(err);
                await this._reportError({
                  error: err,
                  fn: 'uploadAudio',
                });
              }
            } finally {
              if (this.showFiles) {
                this.fetchAudioFiles();
              }
            }
          }
          // reset value to null so we can get the "change" event to run
          // in any case. Even when the user selects the same file
          e.target.value = null;
        }
      });
    },
    async fetchAudioFiles() {
      try {
        this.loading = true;
        const files = await this._s3fetchFiles(`${this._sitePath}/${this.bucketPath}`);
        this.audioFiles = files;
      } catch (err) {
        this.showFiles = false;
        await this._reportError({
          error: err,
          fn: 'fetchAudioFiles',
        });
      } finally {
        this.loading = false;
      }
    },
    setTitle(e) {
      const text = e.currentTarget.value;
      this._debounce(() => {
        const val = text.trim() !== '' ? text : false;
        this.$store.dispatch('setDropletVal', {
          drp: this.droplet,
          key: 'title',
          val,
        });
      }, 200);
    },
    toggleModal() {
      this.confirmModal = !this.confirmModal;
      const content = this.$refs.confirmModal;
      if (this.confirmModal) {
        this.modelMoveOut(content);
      } else {
        this.modalMoveIn(content);
      }
    },
    confirmDelete(file) {
      this.activeAudio = file;
      this.toggleModal();
    },
    async deleteAudioFile() {
      // prep notifcation type
      const notif = {
        id: this.guid(),
      };
      const file = path.basename(this.activeAudio);
      try {
        this.loading = true;
        // delete original
        await this._s3deleteFile({
          key: this.activeAudio,
        });
        // set for notifications
        notif.type = 'success';
        notif.message = `Successfully deleted audio file: ${file}`;
      } catch (err) {
        // set for notifications
        notif.type = 'error';
        notif.message = `${err.message || err}. This has been reported.`;
        await this._reportError({
          error: err,
          fn: 'deleteAudioFile',
        });
      } finally {
        // send off to notifications
        this.$store.dispatch('addNotification', notif);
        // kill loader & refetch files
        this.loading = false;
        this.fetchAudioFiles();
      }
      // if deleting audio that is being used, reset it to default
      if (this.droplet.audioFile === `/${this.bucketPath}${file}`) {
        this.updateAudioSrc(`${process.env.S3_ASSETS_URL}multi-media/sample-audio.mp3`, true);
      }
    },
    async updateAudioSrc(f, def) {
      // if came from click, gather full url
      let file;
      if (def) {
        file = f;
      } else {
        file = `/${this.bucketPath}${this._getFromThumb(path.basename(f))}`;
      }
      // destroy plugin first (parent component method)
      this.$vnode.context.unBindPlugin();
      const payload = {};
      payload.drp = this.droplet;
      payload.audioFile = file;
      this.$store.dispatch('setAudioFile', payload);
      // rebind plugin (parent component method)
      this.$vnode.context.bindPlugin();
    },
    async drzRevertData() {
      // bounce plugin on cancel
      try {
        if (!this._isBoilerplateAsset(this.droplet.audioFile)) {
          let file = path.basename(this.droplet.audioFile);
          file = `${this._sitePath}/${this.bucketPath}${file}`;
          await this._s3checkFile({ path: file });
        }
      } catch (e) {
        this.$log.warn('warning, file not there to revert: ', e);
        // this is for the event in which a user deletes
        // an mp3 from s3 then tries to cancel. Since we
        // aren't restoring the file in s3, we need to
        // reset to the default sound sample
        this.updateAudioSrc(`${process.env.S3_ASSETS_URL}multi-media/sample-audio.mp3`, true);
      } finally {
        this.bouncePlugin();
        this.$emit('css-generator');
      }
    },
    bouncePlugin() {
      this.$vnode.context.unBindPlugin();
      this.$vnode.context.bindPlugin();
    },
    formatErrors(ers) {
      return ers.join(' ');
    },
  },
});



// WEBPACK FOOTER //
// ./src/global-view/editor/droplets/audio-player/components/_edit-options/index.js